import React from 'react';
import { Link } from 'react-router-dom';
import {
    CALL_CONTENT_MAX_LENGTH,
    CALL_DIGIT_CHOICES,
    CALL_INTERACTION_TYPES,
    CALL_LANGUAGE_OPTIONS,
    CALL_OPTION_ACTIONS,
    CALL_PROMPT_MAX_LENGTH,
    CALL_VOICE_OPTIONS,
    DEFAULT_CALL_INTERACTION_TYPE,
    DEFAULT_DTMF_OPTION_ROWS,
} from './callTemplateOptions';

/**
 * Shared chrome + field layout for Create / Edit ECX Call Template pages.
 */
export function AdminCallTemplatePage({
    title,
    subtitle,
    headerBadge,
    backTo = '/admin-templates',
    backLabel = 'Back to templates',
    children,
    footer,
}) {
    return (
        <div className="container container-xxl admin-call-template-page">
            <div className="pagetitle d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <div>
                    <h1 className="mb-1">
                        <i className="bi bi-telephone text-info me-2"></i>
                        {title}
                    </h1>
                    {subtitle ? (
                        <p className="text-muted small mb-0">{subtitle}</p>
                    ) : null}
                </div>
                <Link
                    to={backTo}
                    className="btn btn-outline-secondary btn-sm"
                >
                    <i className="bi bi-arrow-left me-1"></i>
                    {backLabel}
                </Link>
            </div>

            <div className="card shadow-sm border-0 admin-call-template-card">
                <div className="card-header bg-white border-bottom admin-call-template-card__header">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                        <div className="d-flex align-items-center gap-2">
                            <span className="admin-call-template-card__icon">
                                <i className="bi bi-telephone-outbound"></i>
                            </span>
                            <div>
                                <h5 className="card-title mb-0">{title}</h5>
                                <span className="badge admin-templates-medium-badge admin-templates-medium-badge--call">
                                    CALL
                                </span>
                            </div>
                        </div>
                        {headerBadge}
                    </div>
                </div>
                <div className="card-body p-3 p-md-4">
                    {children}
                </div>
                {footer ? (
                    <div className="card-footer bg-white border-top admin-call-template-card__footer">
                        {footer}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

export function AdminCallTemplateMetaGrid({ items }) {
    if (!items?.length) return null;
    return (
        <div className="admin-call-template-meta mb-4">
            <div className="row g-3">
                {items.map((item) => (
                    <div key={item.label} className="col-6 col-md-3">
                        <div className="admin-call-template-meta__item">
                            <div className="admin-call-template-meta__label">{item.label}</div>
                            <div
                                className={`admin-call-template-meta__value ${item.mono ? 'font-monospace small' : ''}`}
                                title={item.title || (typeof item.value === 'string' ? item.value : undefined)}
                            >
                                {item.value ?? '—'}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export function AdminCallTemplateFields({
    formData,
    onChange,
    reminderNameControl,
    usedByDisabled = false,
    hideUsedBy = false,
    contentField = 'content',
}) {
    const contentValue = formData[contentField] || '';

    return (
        <>
            <section className="admin-call-template-section mb-4">
                <h6 className="admin-call-template-section__title">
                    <i className="bi bi-info-circle me-2"></i>
                    Template
                </h6>
                <div className="row g-3">
                    <div className={hideUsedBy ? 'col-12' : 'col-md-6'}>
                        <label className="form-label fw-semibold">Template name *</label>
                        {reminderNameControl}
                    </div>
                    {!hideUsedBy && (
                        <div className="col-md-6">
                            <label className="form-label fw-semibold">Used by *</label>
                            <select
                                className="form-select"
                                name="usedBy"
                                value={formData.usedBy}
                                onChange={onChange}
                                required
                                disabled={usedByDisabled}
                            >
                                <option value="CLINIC">Clinic</option>
                                <option value="ECX">ECX</option>
                            </select>
                            <div className="form-text">
                                {usedByDisabled
                                    ? 'Set automatically from the reminder type'
                                    : 'Who this master template is intended for'}
                            </div>
                        </div>
                    )}
                </div>
            </section>

            <section className="admin-call-template-section mb-4">
                <h6 className="admin-call-template-section__title">
                    <i className="bi bi-soundwave me-2"></i>
                    Voice settings
                </h6>
                <div className="row g-3">
                    <div className="col-md-6">
                        <label className="form-label fw-semibold">Twilio voice *</label>
                        <select
                            className="form-select"
                            name="voice"
                            value={formData.voice}
                            onChange={onChange}
                            required
                        >
                            {CALL_VOICE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        <div className="form-text">
                            Used for Twilio &lt;Say&gt; on live calls
                        </div>
                    </div>
                    <div className="col-md-6">
                        <label className="form-label fw-semibold">Language *</label>
                        <select
                            className="form-select"
                            name="language"
                            value={formData.language}
                            onChange={onChange}
                            required
                        >
                            {CALL_LANGUAGE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        <div className="form-text">
                            Locale / accent for &lt;Say&gt; (e.g. en-US, en-GB)
                        </div>
                    </div>
                </div>
            </section>

            <section className="admin-call-template-section mb-0">
                <h6 className="admin-call-template-section__title">
                    <i className="bi bi-chat-left-text me-2"></i>
                    Call script
                </h6>
                <label className="form-label fw-semibold">Script content *</label>
                <textarea
                    className="form-control admin-call-template-script"
                    rows={10}
                    name={contentField}
                    value={contentValue}
                    onChange={onChange}
                    placeholder="Enter the plain-text script Twilio will speak"
                    maxLength={CALL_CONTENT_MAX_LENGTH}
                    required
                />
                <div className="d-flex flex-wrap justify-content-between gap-2 mt-1">
                    <div className="form-text mb-0">
                        Variables:{" "}
                        <code>{"{PATIENT_NAME}"}</code>,{" "}
                        <code>{"{CLINIC_NAME}"}</code>,{" "}
                        <code>{"{APPOINTMENT_DATE}"}</code>,{" "}
                        <code>{"{APPOINTMENT_TIME}"}</code>
                        {formData.interactionType === 'DTMF' ? (
                            <>
                                . For DTMF, include spoken instructions like
                                “Press 1 to confirm, 2 to cancel, 3 to repeat.”
                            </>
                        ) : null}
                    </div>
                    <div className="form-text mb-0">
                        {contentValue.length}/{CALL_CONTENT_MAX_LENGTH}
                    </div>
                </div>
            </section>
        </>
    );
}

/**
 * Patient input / DTMF option editor for clinic call_template rows.
 */
export function AdminCallTemplateInteractionFields({
    formData,
    onChange,
    optionRows,
    onOptionRowsChange,
}) {
    const interactionType = formData.interactionType || DEFAULT_CALL_INTERACTION_TYPE;
    const isDtmf = interactionType === 'DTMF';

    const handleInteractionTypeChange = (e) => {
        const nextType = e.target.value;
        onChange({ target: { name: 'interactionType', value: nextType } });
        if (nextType === 'DTMF' && (!optionRows || optionRows.length === 0)) {
            onOptionRowsChange(DEFAULT_DTMF_OPTION_ROWS.map((row) => ({ ...row })));
        }
        if (nextType === 'NONE') {
            onOptionRowsChange([]);
        }
    };

    const updateRow = (index, patch) => {
        onOptionRowsChange(
            optionRows.map((row, i) => (i === index ? { ...row, ...patch } : row))
        );
    };

    const removeRow = (index) => {
        onOptionRowsChange(optionRows.filter((_, i) => i !== index));
    };

    const addRow = () => {
        const used = new Set(optionRows.map((row) => row.digit));
        const nextDigit = CALL_DIGIT_CHOICES.find((digit) => !used.has(digit)) || '1';
        onOptionRowsChange([
            ...optionRows,
            { digit: nextDigit, action: 'CONFIRM', say: '' },
        ]);
    };

    return (
        <section className="admin-call-template-section mt-4 mb-0">
            <h6 className="admin-call-template-section__title">
                <i className="bi bi-dialpad me-2"></i>
                Patient interaction
            </h6>

            <div className="mb-3">
                <label className="form-label fw-semibold">Interaction type *</label>
                <select
                    className="form-select"
                    name="interactionType"
                    value={interactionType}
                    onChange={handleInteractionTypeChange}
                    required
                >
                    {CALL_INTERACTION_TYPES.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
                <div className="form-text">
                    {CALL_INTERACTION_TYPES.find((o) => o.value === interactionType)?.help}
                </div>
            </div>

            {isDtmf && (
                <>
                    <div className="admin-call-template-options mb-3">
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                            <label className="form-label fw-semibold mb-0">Digit options</label>
                            <button
                                type="button"
                                className="btn btn-outline-info btn-sm"
                                onClick={addRow}
                                disabled={optionRows.length >= CALL_DIGIT_CHOICES.length}
                            >
                                <i className="bi bi-plus-lg me-1"></i>
                                Add option
                            </button>
                        </div>

                        {optionRows.length === 0 ? (
                            <div className="alert alert-warning admin-call-template-alert mb-0 py-2">
                                Add at least one digit option for DTMF calls.
                            </div>
                        ) : (
                            <div className="d-flex flex-column gap-3">
                                {optionRows.map((row, index) => (
                                    <div
                                        key={`option-${index}`}
                                        className="admin-call-template-option-row"
                                    >
                                        <div className="row g-2 align-items-start">
                                            <div className="col-4 col-md-2">
                                                <label className="form-label small mb-1">Digit</label>
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={row.digit}
                                                    onChange={(e) =>
                                                        updateRow(index, { digit: e.target.value })
                                                    }
                                                >
                                                    {CALL_DIGIT_CHOICES.map((digit) => (
                                                        <option key={digit} value={digit}>
                                                            {digit}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-8 col-md-3">
                                                <label className="form-label small mb-1">Action</label>
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={row.action}
                                                    onChange={(e) =>
                                                        updateRow(index, { action: e.target.value })
                                                    }
                                                >
                                                    {CALL_OPTION_ACTIONS.map((action) => (
                                                        <option key={action.value} value={action.value}>
                                                            {action.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-12 col-md-6">
                                                <label className="form-label small mb-1">
                                                    Reply after press (optional)
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    value={row.say}
                                                    maxLength={CALL_PROMPT_MAX_LENGTH}
                                                    placeholder="Spoken confirmation, or blank for REPEAT"
                                                    onChange={(e) =>
                                                        updateRow(index, { say: e.target.value })
                                                    }
                                                />
                                            </div>
                                            <div className="col-12 col-md-1 d-flex align-items-end">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-danger btn-sm w-100"
                                                    title="Remove option"
                                                    onClick={() => removeRow(index)}
                                                    disabled={optionRows.length <= 1}
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="row g-3">
                        <div className="col-md-6">
                            <label className="form-label fw-semibold">Invalid digit prompt</label>
                            <textarea
                                className="form-control"
                                rows={3}
                                name="invalidPrompt"
                                value={formData.invalidPrompt || ''}
                                onChange={onChange}
                                maxLength={CALL_PROMPT_MAX_LENGTH}
                                placeholder="Sorry, that option is not valid. Please try again."
                            />
                            <div className="form-text">
                                {(formData.invalidPrompt || '').length}/{CALL_PROMPT_MAX_LENGTH}
                            </div>
                        </div>
                        <div className="col-md-6">
                            <label className="form-label fw-semibold">No input / timeout prompt</label>
                            <textarea
                                className="form-control"
                                rows={3}
                                name="timeoutPrompt"
                                value={formData.timeoutPrompt || ''}
                                onChange={onChange}
                                maxLength={CALL_PROMPT_MAX_LENGTH}
                                placeholder="We did not receive your selection. Goodbye."
                            />
                            <div className="form-text">
                                {(formData.timeoutPrompt || '').length}/{CALL_PROMPT_MAX_LENGTH}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </section>
    );
}

export function AdminCallTemplateFooter({
    left,
    onCancel,
    onSubmitLabel,
    submitting,
    submitDisabled,
    submitVariant = 'info',
}) {
    return (
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div>{left}</div>
            <div className="d-flex flex-wrap gap-2">
                <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={onCancel}
                    disabled={submitting}
                >
                    <i className="bi bi-x-lg me-1"></i>
                    Cancel
                </button>
                <button
                    type="submit"
                    className={`btn btn-${submitVariant} btn-sm text-white`}
                    disabled={submitDisabled || submitting}
                >
                    {submitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Saving…
                        </>
                    ) : (
                        <>
                            <i className="bi bi-check-lg me-1"></i>
                            {onSubmitLabel}
                        </>
                    )}
                </button>
            </div>
        </div>
    );
}
